import { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Pencil, Upload } from "lucide-react";
import api, { apiError, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", subcategories: [], image_url: "", order: 0, active: true };

export default function AdminCategories() {
    const [cats, setCats] = useState([]);
    const [editing, setEditing] = useState(null);
    const fileRef = useRef();

    const load = () => api.get("/admin/categories").then(({ data }) => setCats(data.categories));
    useEffect(() => { load(); }, []);

    const save = async () => {
        const payload = { ...editing, subcategories: typeof editing.subcategories === "string" ? editing.subcategories.split(",").map((s) => s.trim()).filter(Boolean) : editing.subcategories };
        try {
            if (editing.id) await api.put(`/admin/categories/${editing.id}`, payload);
            else await api.post("/admin/categories", payload);
            toast.success("Category saved"); setEditing(null); load();
        } catch (err) { toast.error(apiError(err)); }
    };
    const remove = async (id) => { if (!window.confirm("Delete category?")) return; await api.delete(`/admin/categories/${id}`); load(); };

    const uploadImg = async (file) => {
        const fd = new FormData(); fd.append("file", file);
        try { const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setEditing((e) => ({ ...e, image_url: data.url })); toast.success("Image uploaded"); }
        catch (err) { toast.error(apiError(err)); }
    };

    const inp = "w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-gold";
    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-semibold">Categories</h1>
                <button data-testid="add-category" onClick={() => setEditing({ ...EMPTY })} className="bg-ink text-white px-4 py-2.5 rounded text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Add</button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cats.map((c) => (
                    <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                            <div><p className="font-medium">{c.name}</p><p className="text-xs text-gray-400">{c.subcategories?.join(", ") || "No subcategories"}</p></div>
                            <div className="flex gap-1"><button onClick={() => setEditing({ ...c })} className="p-1.5 hover:bg-gray-100 rounded"><Pencil className="h-4 w-4" /></button><button onClick={() => remove(c.id)} className="p-1.5 hover:bg-gray-100 rounded text-red-500"><Trash2 className="h-4 w-4" /></button></div>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} Category</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <input data-testid="category-name" placeholder="Category name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inp} />
                            <input placeholder="Subcategories (comma separated)" value={Array.isArray(editing.subcategories) ? editing.subcategories.join(", ") : editing.subcategories} onChange={(e) => setEditing({ ...editing, subcategories: e.target.value })} className={inp} />
                            <div className="flex items-center gap-3">
                                {editing.image_url && <img src={mediaUrl(editing.image_url)} alt="" className="h-14 w-14 object-cover rounded" />}
                                <button onClick={() => fileRef.current.click()} className="border border-gray-300 rounded px-3 py-2 text-sm flex items-center gap-2"><Upload className="h-4 w-4" /> Image</button>
                                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadImg(e.target.files[0])} />
                            </div>
                            <button data-testid="category-save" onClick={save} className="w-full bg-ink text-white py-2.5 rounded text-sm">Save</button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
